import { useState, useEffect, useCallback } from 'react'
import {
  Home,
  FileText,
  ClipboardList,
  PieChart,
  Target,
  Settings,
  CheckCircle2,
  Circle,
  ChevronDown,
  ChevronUp,
  Loader2,
  BookOpen,
} from 'lucide-react'
import { clsx } from 'clsx'
import { useAuthStore } from '@store/authStore'
import { preferencesApi } from '@api/client'
import { showToast } from '@store/uiStore'

// ─── Module Definitions ────────────────────────────────────

interface ModuleInfo {
  id: string
  name: string
  icon: typeof Home
  iconColor: string
  summary: string
  details: string[]
  keyFeatures: string[]
}

const MODULES: ModuleInfo[] = [
  {
    id: 'home',
    name: 'Home / Sessions',
    icon: Home,
    iconColor: 'text-blue-500',
    summary:
      'The Home page is your starting point. Search for HubSpot deals and create DealHub sessions to track your pre-sales work.',
    details: [
      'A "session" is a workspace linked to a HubSpot deal where you build quotes, scope hours, and calculate ROI.',
      'Search for any HubSpot deal by name — once selected, you can create a new session or open an existing one.',
      'The sessions table shows all your recent sessions with module icons that light up (turn blue) when you\'ve started working on that module (Quote, Scope, or ROI).',
      'Each session tracks who created it and when it was last modified.',
      'You can create multiple sessions for the same deal if you need to explore different pricing or scoping scenarios.',
    ],
    keyFeatures: [
      'HubSpot deal search integration',
      'Session creation and management',
      'Module progress indicators (lit-up icons)',
      'Quick stats showing total sessions, quotes, and scopes',
    ],
  },
  {
    id: 'quote',
    name: 'Quote Builder',
    icon: FileText,
    iconColor: 'text-emerald-500',
    summary:
      'Build detailed pricing quotes with product line items, platform fees, and financial analysis.',
    details: [
      'The Quote Builder lets you configure all pricing aspects of a deal: products, platform fees, implementation costs, and contract terms.',
      'Add products from the product catalog and customize quantities, pricing tiers, and discounts.',
      'Financial metrics (MRR, ARR, TCV, gross profit margin, LTV/CAC ratio, payback period) are calculated automatically.',
      'Quotes can be synced from Scope — once you finish scoping, the implementation hours and costs carry over.',
      'Use the Review tab to get an AI-powered assessment of the deal\'s financial health.',
    ],
    keyFeatures: [
      'Product catalog with pricing tiers',
      'Automatic financial metric calculations',
      'Scope-to-Quote sync for implementation fees',
      'AI-powered deal review and assessment',
      'Export-ready quote format',
    ],
  },
  {
    id: 'scope',
    name: 'Scope / Hours Calculator',
    icon: ClipboardList,
    iconColor: 'text-purple-500',
    summary:
      'Estimate implementation hours across all product areas: KYC, AML, Biometrics/IDV, KYB, Recipes, and Rules.',
    details: [
      'The Scope module walks you through each product area with configurable options that drive hour estimates.',
      'Start with the General tab to set up basics like integration type, environment needs, and timeline.',
      'Each product tab (KYC, AML, Biometrics, KYB) has specific configuration options that auto-calculate hours.',
      'The Recipes and Rules tabs cover workflow configuration and business rule setup.',
      'Hours are broken down into categories: auto-calculated hours (from configs), manual adjustments, and feature request hours.',
      'A contingency percentage is applied to the total for buffer. Total cost = total hours × hourly rate.',
    ],
    keyFeatures: [
      'Per-module hour configuration',
      'Automatic hour calculations based on scope choices',
      'Manual hour adjustments and overrides',
      'Contingency percentage buffer',
      'Sync to Quote for implementation fees',
    ],
  },
  {
    id: 'roi',
    name: 'ROI Calculator',
    icon: PieChart,
    iconColor: 'text-orange-500',
    summary:
      'Model return on investment scenarios to demonstrate value to prospects across different regions and use cases.',
    details: [
      'The ROI Calculator helps you build a compelling business case for prospects by modelling the financial impact of the platform.',
      'Configure region-specific parameters like verification volumes, manual review costs, and fraud rates.',
      'The flow visualization shows how verifications move through the system and where cost savings occur.',
      'Compare current-state costs (manual processes, fraud losses) against future-state costs (with automation).',
      'Generate shareable ROI reports that demonstrate payback period and total savings.',
    ],
    keyFeatures: [
      'Multi-region cost modelling',
      'Flow visualization of verification processes',
      'Before/after cost comparison',
      'Payback period calculations',
      'Shareable ROI reports',
    ],
  },
  {
    id: 'target-accounts',
    name: 'Target Accounts',
    icon: Target,
    iconColor: 'text-rose-500',
    summary:
      'Track and prioritize target accounts with signal tracking, ICP scoring, and AI-powered research assistance.',
    details: [
      'Import and manage a list of target accounts with ICP (Ideal Customer Profile) scoring.',
      'Signal tracking monitors buying signals like funding rounds, leadership changes, compliance events, and tech adoption.',
      'Each account has a composite score based on ICP fit, buying signals, and engagement level.',
      'The AI Assistant can research accounts, generate outreach sequences, analyze pain points, and create personalized messaging.',
      'Configurable columns let you customize which data points are visible in the table view.',
    ],
    keyFeatures: [
      'ICP scoring and prioritization',
      'Buying signal tracking and scoring',
      'AI-powered account research and outreach',
      'Customizable table columns',
      'Voice-enabled AI assistant',
    ],
  },
  {
    id: 'settings',
    name: 'Settings (Admin)',
    icon: Settings,
    iconColor: 'text-gray-500',
    summary:
      'Admin-only area for configuring scope hour defaults, pricing formulas, managing products, and viewing audit logs.',
    details: [
      'Settings is restricted to admin users and controls platform-wide configuration.',
      'Scope Hours: Set default hour values for each product area that pre-populate when creating new scopes.',
      'Formulas: Configure the pricing and calculation formulas used across the platform.',
      'Products: Manage the product catalog including pricing tiers and descriptions.',
      'Audit Log: View a chronological record of all changes made across all deals.',
      'Roadmap: View and manage feedback tickets submitted by users (bugs and feature requests).',
    ],
    keyFeatures: [
      'Scope hour default configuration',
      'Formula management',
      'Product catalog management',
      'Audit trail for all changes',
      'Feedback roadmap kanban board',
    ],
  },
]

// ─── Module Card ───────────────────────────────────────────

function ModuleCard({
  module,
  isAcknowledged,
  onToggleAcknowledge,
}: {
  module: ModuleInfo
  isAcknowledged: boolean
  onToggleAcknowledge: (moduleId: string) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const Icon = module.icon

  return (
    <div
      className={clsx(
        'border rounded-lg transition-all',
        isAcknowledged
          ? 'border-green-500/30 bg-green-500/5'
          : 'border-[var(--border-color)] bg-[var(--bg-primary)]'
      )}
    >
      {/* Header */}
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div
            className={clsx(
              'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
              isAcknowledged ? 'bg-green-500/10' : 'bg-[var(--bg-tertiary)]'
            )}
          >
            <Icon size={20} className={isAcknowledged ? 'text-green-500' : module.iconColor} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-[var(--text-primary)]">{module.name}</h3>
              {isAcknowledged && (
                <CheckCircle2 size={14} className="text-green-500 flex-shrink-0" />
              )}
            </div>
            <p className="text-xs text-[var(--text-secondary)] mt-1">{module.summary}</p>
          </div>

          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors flex-shrink-0"
          >
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-[var(--border-color)] pt-3">
          <div>
            <h4 className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide mb-2">
              How It Works
            </h4>
            <ul className="space-y-1.5">
              {module.details.map((detail, idx) => (
                <li key={idx} className="text-xs text-[var(--text-secondary)] flex gap-2">
                  <span className="text-[var(--text-muted)] flex-shrink-0 mt-0.5">•</span>
                  {detail}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide mb-2">
              Key Features
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {module.keyFeatures.map((feature, idx) => (
                <span
                  key={idx}
                  className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--bg-tertiary)] text-[var(--text-secondary)]"
                >
                  {feature}
                </span>
              ))}
            </div>
          </div>

          {/* Acknowledge Button */}
          <button
            onClick={() => onToggleAcknowledge(module.id)}
            className={clsx(
              'flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg transition-colors',
              isAcknowledged
                ? 'bg-green-500/10 text-green-500 hover:bg-green-500/20'
                : 'bg-primary/10 text-primary hover:bg-primary/20'
            )}
          >
            {isAcknowledged ? (
              <>
                <CheckCircle2 size={14} />
                Acknowledged — I understand this module
              </>
            ) : (
              <>
                <Circle size={14} />
                Mark as understood
              </>
            )}
          </button>
        </div>
      )}
    </div>
  )
}

// ─── How It Works Tab ──────────────────────────────────────

export function HowItWorksTab() {
  const { user } = useAuthStore()
  const [acknowledged, setAcknowledged] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  // Load user's acknowledgment state
  useEffect(() => {
    const loadPrefs = async () => {
      if (!user?.email) return
      try {
        const response = await preferencesApi.get(user.email)
        if (response.success && response.data?.preferences) {
          setAcknowledged(response.data.preferences.onboarding_acknowledged || [])
        }
      } catch {
        console.error('Failed to load onboarding state')
      } finally {
        setIsLoading(false)
      }
    }
    loadPrefs()
  }, [user?.email])

  const handleToggleAcknowledge = useCallback(
    async (moduleId: string) => {
      if (!user?.email) return

      const newAcknowledged = acknowledged.includes(moduleId)
        ? acknowledged.filter((id) => id !== moduleId)
        : [...acknowledged, moduleId]

      setAcknowledged(newAcknowledged)
      setIsSaving(true)

      try {
        await preferencesApi.save({
          user_id: user.email,
          user_email: user.email,
          onboarding_acknowledged: newAcknowledged,
        })

        if (!acknowledged.includes(moduleId)) {
          showToast(`Got it! "${MODULES.find((m) => m.id === moduleId)?.name}" acknowledged`, 'success')
        }
      } catch {
        // Revert on error
        setAcknowledged(acknowledged)
        showToast('Failed to save', 'error')
      } finally {
        setIsSaving(false)
      }
    },
    [acknowledged, user?.email]
  )

  const acknowledgedCount = acknowledged.length
  const totalModules = MODULES.length
  const allAcknowledged = acknowledgedCount === totalModules

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={24} className="animate-spin text-[var(--text-muted)]" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Progress Banner */}
      <div
        className={clsx(
          'rounded-lg p-4 border',
          allAcknowledged
            ? 'bg-green-500/5 border-green-500/30'
            : 'bg-[var(--bg-secondary)] border-[var(--border-color)]'
        )}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BookOpen size={20} className={allAcknowledged ? 'text-green-500' : 'text-primary'} />
            <div>
              <h2 className="text-sm font-semibold text-[var(--text-primary)]">
                {allAcknowledged ? 'All modules reviewed!' : 'Learn how DealHub works'}
              </h2>
              <p className="text-xs text-[var(--text-muted)] mt-0.5">
                {allAcknowledged
                  ? 'You\'ve reviewed and acknowledged all modules. You\'re all set!'
                  : 'Expand each module below to learn what it does, then mark it as understood.'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-xs text-[var(--text-muted)]">
              {acknowledgedCount}/{totalModules}
            </div>
            {isSaving && <Loader2 size={12} className="animate-spin text-[var(--text-muted)]" />}
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-3 w-full bg-[var(--bg-tertiary)] rounded-full h-1.5">
          <div
            className={clsx(
              'h-1.5 rounded-full transition-all duration-500',
              allAcknowledged ? 'bg-green-500' : 'bg-primary'
            )}
            style={{ width: `${(acknowledgedCount / totalModules) * 100}%` }}
          />
        </div>
      </div>

      {/* Module Cards */}
      <div className="space-y-3">
        {MODULES.map((module) => (
          <ModuleCard
            key={module.id}
            module={module}
            isAcknowledged={acknowledged.includes(module.id)}
            onToggleAcknowledge={handleToggleAcknowledge}
          />
        ))}
      </div>
    </div>
  )
}
